// hooks/useStoreManagerships.ts
//
// Returns the current user's store-manager memberships — the set
// of stores they can act on behalf of, and what permissions they
// have for each. Subscribes live to /users/{uid}/storeManagerships
// so revocation by the store owner takes effect immediately
// without the user reloading.
//
// Three convenience derived flags:
//
//   - hasAny:                   true if the user has at least one
//                               storeManagership entry.
//   - canCreateTournaments:     true if ANY of the user's
//                               memberships grants
//                               canCreateAndEditTournaments.
//   - canManageSeries:          same shape for canManageSeries.
//
// These three drive 'should this menu/route be available?' checks
// without forcing consumers to iterate the list themselves.

import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { subscribeUserStoreManagerships } from '../services/storeManagerService';
import type { StoreManagership } from '../types/storeManager.types';

export interface UseStoreManagershipsResult {
  memberships: StoreManagership[];
  /** True while the initial subscription snapshot is in flight.
   *  Consumers should treat "permitted" as false during loading. */
  loading: boolean;
  hasAny: boolean;
  canCreateTournaments: boolean;
  canManageSeries: boolean;
  /** Lookup helper — returns the membership for a specific store,
   *  or undefined if the user isn't a proxy for that store. */
  forStore: (storeId: string) => StoreManagership | undefined;
}

export function useStoreManagerships(): UseStoreManagershipsResult {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<StoreManagership[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setMemberships([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeUserStoreManagerships(user.uid, (rows) => {
      setMemberships(rows);
      setLoading(false);
    });
    return () => unsub();
  }, [user?.uid]);

  const derived = useMemo(() => {
    const hasAny = memberships.length > 0;
    const canCreateTournaments = memberships.some(
      (m) => m.permissions.canCreateAndEditTournaments,
    );
    const canManageSeries = memberships.some(
      (m) => m.permissions.canManageSeries,
    );
    const byStore = new Map(memberships.map((m) => [m.storeId, m]));

    // Dev-time debug hook so the operator can verify in DevTools
    // what the hook is actually seeing. No-op in production.
    if (typeof window !== 'undefined') {
      (window as unknown as { __storeManagerships?: unknown }).__storeManagerships = {
        memberships,
        hasAny,
        canCreateTournaments,
        canManageSeries,
      };
    }

    return {
      hasAny,
      canCreateTournaments,
      canManageSeries,
      forStore: (storeId: string) => byStore.get(storeId),
    };
  }, [memberships]);

  return { memberships, loading, ...derived };
}
